"use client";

import { useEffect, useRef, useState } from 'react';
import '@xterm/xterm/css/xterm.css';

type TerminalInstance = {
  clear: () => void;
  dispose: () => void;
  open: (element: HTMLElement) => void;
  reset: () => void;
  write: (data: string) => void;
};

type TmuxSnapshotResponse = {
  ok: boolean;
  snapshot?: {
    chatId: string;
    tmuxSession: string;
    tmuxAlive: boolean;
    paneCurrentCommand: string | null;
    content: string;
    capturedAt: string;
  };
  error?: string;
};

function normalizeTerminalContent(content: string): string {
  if (!content) {
    return '';
  }
  return content.replace(/\r?\n/g, '\r\n');
}

export default function TmuxLiveTerminal({ chatId }: { chatId: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<TerminalInstance | null>(null);
  const lastContentRef = useRef('');
  const [statusText, setStatusText] = useState('Dang ket noi tmux...');

  useEffect(() => {
    let disposed = false;
    let intervalId: number | null = null;

    async function loadSnapshot() {
      try {
        const response = await fetch(`/api/telegram-codex/tmux?chatId=${encodeURIComponent(chatId)}`, {
          cache: 'no-store',
        });
        const data = (await response.json()) as TmuxSnapshotResponse;
        if (!response.ok || !data.ok || !data.snapshot) {
          setStatusText(data.error || 'Khong doc duoc tmux snapshot.');
          return;
        }

        const nextContent = normalizeTerminalContent(data.snapshot.content || '');
        setStatusText(data.snapshot.tmuxAlive ? 'Tmux dang song' : 'Tmux dang dung');

        if (!terminalRef.current || nextContent === lastContentRef.current) {
          return;
        }

        terminalRef.current.reset();
        terminalRef.current.write(nextContent || 'Tmux chua co output.\r\n');
        lastContentRef.current = nextContent;
      } catch (error) {
        setStatusText(error instanceof Error ? error.message : 'Khong ket noi duoc tmux.');
      }
    }

    async function initTerminal() {
      if (!containerRef.current) {
        return;
      }

      const { Terminal } = await import('@xterm/xterm');
      if (disposed) {
        return;
      }

      const terminal = new Terminal({
        cursorBlink: false,
        disableStdin: true,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 13,
        rows: 28,
        theme: {
          background: '#020617',
          foreground: '#dbeafe',
          cursor: '#38bdf8',
          black: '#0f172a',
          green: '#34d399',
          blue: '#60a5fa',
          yellow: '#fbbf24',
          red: '#f87171',
        },
      }) as TerminalInstance;

      terminal.open(containerRef.current);
      terminal.write('Dang tai man hinh tmux...\r\n');
      terminalRef.current = terminal;

      await loadSnapshot();
      intervalId = window.setInterval(() => {
        void loadSnapshot();
      }, 1500);
    }

    void initTerminal();

    return () => {
      disposed = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      terminalRef.current?.dispose();
      terminalRef.current = null;
      lastContentRef.current = '';
    };
  }, [chatId]);

  return (
    <div
      style={{
        marginTop: 14,
        background: '#020617',
        borderRadius: 16,
        border: '1px solid #0f172a',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
          color: '#bfdbfe',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          fontSize: 12,
        }}
      >
        <strong>Live tmux terminal</strong>
        <span>{statusText}</span>
      </div>
      <div ref={containerRef} style={{ minHeight: 360, padding: 8 }} />
    </div>
  );
}
